import { merge } from 'lodash-es'

import type { GraphEvent, GraphHooks } from '../types/events'
import type { Graph } from './graph'

export type PluginHooks<O, S> = GraphHooks<
  (this: Plugin<O, S>, event: GraphEvent, graph: Graph) => void
>

export interface PluginConfig<O, S> extends PluginHooks<O, S> {
  name: string
  /**
   * 添加配置
   */
  addOptions?(this: Plugin<O, S>): O
  /**
   * 添加存储
   */
  addStorage?(this: Plugin<O, S>): S
  /**
   * 插件初始化钩子
   */
  onPluginInit?(this: Plugin<O, S>, graph: Graph): void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Plugin<O = any, S = any> implements PluginConfig<O, S> {
  /**
   * 插件名称
   */
  name: string
  /**
   * 存储对象
   */
  storage: S
  /**
   * 配置对象
   */
  options: O
  /**
   * 事件钩子
   */
  hooks: PluginHooks<O, S>
  /**
   * 插件初始化钩子
   */
  onPluginInit: PluginConfig<O, S>['onPluginInit']

  constructor(config: PluginConfig<O, S>) {
    const { name, addStorage, addOptions, onPluginInit, ...hooks } = config

    this.name = name
    this.storage = addStorage ? addStorage.call(this) : ({} as S)
    this.options = addOptions ? addOptions.call(this) : ({} as O)
    this.onPluginInit = onPluginInit

    this.hooks = Object.keys(hooks).reduce<PluginHooks<O, S>>((acc, key) => {
      const handler = (hooks as GraphHooks)[key as keyof typeof hooks]

      if (handler) {
        acc[key as keyof typeof acc] = handler.bind(this)
      }

      return acc
    }, {})
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static create = <O = any, S = any>(config: PluginConfig<O, S>) => {
    return (options?: Partial<O>, name?: string) => {
      const plugin = new Plugin<O, S>(config)

      if (options) {
        plugin.setOptions(options)
      }

      if (name) {
        plugin.name = name
      }

      return plugin
    }
  }

  /**
   * 更新存储值
   *
   * @param storage
   */
  setStorage(storage: Partial<S>) {
    this.storage = merge(this.storage, storage)
  }

  /**
   * 更新插件配置
   */
  setOptions(options: Partial<O>) {
    this.options = merge(this.options, options)
  }
}
